import { CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { databaseService } from '../services/database';
import { insightsService, mapIncidentRow } from '../services/insights';
import {
  EventCategory,
  InsightEvent,
  MetricKey,
  RouterTimeline,
  RunBand,
  TimelineSeries,
} from '../types/insights';
import { Operator, PCInstance } from '../types/database';
import UPlotChart, {
  AlignedChartData,
  ChartBand,
  ChartMarker,
  ChartSeriesConfig,
} from '../components/insights/UPlotChart';
import RunSceneStrip from '../components/insights/RunSceneStrip';
import EventMarkerRow, { CATEGORY_STYLE } from '../components/insights/EventMarkerRow';
import EventDetailDrawer from '../components/insights/EventDetailDrawer';
import ScenesTab from '../components/insights/ScenesTab';
import TrendsTab from '../components/insights/TrendsTab';

// ---------------------------------------------------------------------------
// InsightsView — admin-gated room-day timeline + correlation views.
//
// Timeline tab (flagship): per-PC telemetry series (telemetry.insights_timeline),
// run/scene bands (insights_runs), unified event markers (room_events) and
// router RSSI/channel-util overlays (insights_router_timeline) on one shared,
// zoomable time axis. Zoom tiers are server-side: >3 h -> 60 s buckets
// (samples_1m), 10 min–3 h -> 10 s, <10 min -> raw 1 s.
//
// All DB timestamps are UTC; the axis/strips/drawer display local time (the
// drawer also shows UTC).
// ---------------------------------------------------------------------------

interface InsightsViewProps {
  roomId?: string;
  operator: Operator | null;
}

// ---- shared styles (TestLabView visual language) ---------------------------

const card: CSSProperties = {
  background: '#fff',
  border: '1px solid #e4e9f0',
  borderRadius: 12,
  padding: 20,
  marginBottom: 16,
};

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: '#54626f',
  marginBottom: 4,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const selectStyle: CSSProperties = {
  padding: '6px 8px',
  border: '1px solid #d5dce5',
  borderRadius: 8,
  fontSize: 13,
};

// ---- metric definitions -----------------------------------------------------

interface MetricDef {
  label: string;
  unit: string;
  /** Index into an insights_timeline point row (PC metrics). */
  idx: number;
  src: 'pc' | 'stations' | 'radios';
}

const METRIC_DEFS: Record<MetricKey, MetricDef> = {
  fps:       { label: 'FPS',            unit: 'fps',  idx: 1,  src: 'pc' },
  fps_min:   { label: 'FPS min',        unit: 'fps',  idx: 2,  src: 'pc' },
  frame_ms:  { label: 'Frame time max', unit: 'ms',   idx: 3,  src: 'pc' },
  bitrate:   { label: 'Bitrate',        unit: 'Mbps', idx: 4,  src: 'pc' },
  latency:   { label: 'Stream latency', unit: 'ms',   idx: 5,  src: 'pc' },
  transmit:  { label: 'Transmit',       unit: 'ms',   idx: 6,  src: 'pc' },
  net_sent:  { label: 'NIC sent',       unit: 'Mbps', idx: 7,  src: 'pc' },
  net_recv:  { label: 'NIC recv',       unit: 'Mbps', idx: 8,  src: 'pc' },
  gpu:       { label: 'GPU load',       unit: '%',    idx: 9,  src: 'pc' },
  gpu_temp:  { label: 'GPU temp',       unit: '°C',   idx: 10, src: 'pc' },
  cpu:       { label: 'CPU load',       unit: '%',    idx: 11, src: 'pc' },
  battery:   { label: 'HMD battery',    unit: '%',    idx: 12, src: 'pc' },
  rssi:      { label: 'RSSI',           unit: 'dBm',  idx: 1,  src: 'stations' },
  chan_util: { label: 'Channel util',   unit: '%',    idx: 1,  src: 'radios' },
};

const METRIC_ORDER: MetricKey[] = [
  'fps', 'fps_min', 'frame_ms', 'bitrate', 'latency', 'transmit',
  'net_sent', 'net_recv', 'gpu', 'gpu_temp', 'cpu', 'battery',
  'rssi', 'chan_util',
];

const PC_COLORS = [
  '#2B6AC0', '#2F7D53', '#C05B2B', '#6d3fa8', '#0e7f7f',
  '#b3468f', '#b47d0e', '#c0392b', '#4b5f77', '#7f8c2b',
];

const CHIP_ORDER: EventCategory[] = [
  'disconnect', 'gap', 'screendoor_suspect', 'render_degraded', 'operator_issue',
  'freeze', 'crash', 'router_settings', 'channel_change', 'router_login',
  'wifi', 'test_run',
];

const DEFAULT_FILTERS: EventCategory[] = CHIP_ORDER.filter((c) => c !== 'wifi');

// ---- helpers ---------------------------------------------------------------

const fmtDateLocal = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const fmtHM = (sec: number) =>
  new Date(sec * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/** Align irregular per-series [ts, ...values] points onto one shared x axis. */
function buildAligned(list: { points: (number | null)[][]; idx: number }[]): AlignedChartData {
  const tsSet = new Set<number>();
  for (const s of list) {
    for (const p of s.points) {
      const t = p[0];
      if (typeof t === 'number') tsSet.add(t);
    }
  }
  const xs = Array.from(tsSet).sort((a, b) => a - b);
  const pos = new Map<number, number>();
  xs.forEach((t, i) => pos.set(t, i));
  const ys = list.map((s) => {
    const arr: (number | null)[] = new Array(xs.length).fill(null);
    for (const p of s.points) {
      const t = p[0];
      if (typeof t === 'number') {
        const i = pos.get(t);
        if (i !== undefined) arr[i] = (p[s.idx] as number | null) ?? null;
      }
    }
    return arr;
  });
  return [xs, ...ys] as AlignedChartData;
}

function Skeleton({ height }: { height: number }) {
  return (
    <div
      style={{
        height,
        borderRadius: 8,
        background: '#e7ecf2',
        animation: 'insightsPulse 1.2s ease-in-out infinite',
        marginBottom: 8,
      }}
    />
  );
}

interface PaneSpec {
  series: ChartSeriesConfig[];
  data: AlignedChartData;
  yLabel: string;
  y2Label?: string;
}

// ---------------------------------------------------------------------------
// Timeline tab
// ---------------------------------------------------------------------------

function TimelineTab({ roomId }: { roomId: string }) {
  const todayStr = fmtDateLocal(new Date());
  const [date, setDate] = useState<string>(todayStr);
  const [range, setRange] = useState<[number, number] | null>(null);
  const [metricA, setMetricA] = useState<MetricKey>('fps');
  const [metricB, setMetricB] = useState<MetricKey | 'none'>('none');
  const [hiddenPcs, setHiddenPcs] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<Set<EventCategory>>(new Set(DEFAULT_FILTERS));
  const [selectedEvent, setSelectedEvent] = useState<InsightEvent | null>(null);

  const [pcs, setPcs] = useState<PCInstance[]>([]);
  const [timeline, setTimeline] = useState<TimelineSeries[]>([]);
  const [runs, setRuns] = useState<RunBand[]>([]);
  const [events, setEvents] = useState<InsightEvent[]>([]);
  const [router, setRouter] = useState<RouterTimeline>({ stations: [], radios: [] });
  const [loading, setLoading] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);

  // Day bounds (local midnights — DST-safe via setDate).
  const dayStartSec = useMemo(() => new Date(`${date}T00:00:00`).getTime() / 1000, [date]);
  const dayEndSec = useMemo(() => {
    const d = new Date(`${date}T00:00:00`);
    d.setDate(d.getDate() + 1);
    return d.getTime() / 1000;
  }, [date]);

  const from = range ? range[0] : dayStartSec;
  const to = range ? range[1] : dayEndSec;
  const durS = to - from;
  // Zoom tiers: >3 h -> 60 s buckets (samples_1m); 10 min–3 h -> 10 s; <10 min -> raw 1 s.
  const bucket = durS > 3 * 3600 ? 60 : durS >= 600 ? 10 : 1;
  const wifiChipOn = filters.has('wifi');
  const includeRawWifi = durS < 7200 && wifiChipOn;
  const isToday = date === todayStr;

  // PC list for toggles / lane order.
  useEffect(() => {
    let cancelled = false;
    databaseService.getPCInstancesInRoom(roomId).then((list) => {
      if (!cancelled) setPcs(list);
    });
    return () => {
      cancelled = true;
    };
  }, [roomId]);

  // Main fetch — series + runs + events + router, in parallel, per range.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const fromIso = new Date(from * 1000).toISOString();
    const toIso = new Date(to * 1000).toISOString();
    Promise.all([
      insightsService.getTimeline(roomId, fromIso, toIso, bucket),
      insightsService.getRuns(roomId, fromIso, toIso),
      insightsService.getEvents(roomId, fromIso, toIso, includeRawWifi),
      insightsService.getRouterTimeline(roomId, fromIso, toIso, bucket),
    ]).then(([tl, rb, ev, rt]) => {
      if (cancelled) return;
      setTimeline(tl);
      setRuns(rb);
      setEvents(ev);
      setRouter(rt);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [roomId, from, to, bucket, includeRawWifi, refreshTick]);

  // Today view: 60 s refetch interval.
  useEffect(() => {
    if (!isToday) return;
    const t = setInterval(() => setRefreshTick((n) => n + 1), 60000);
    return () => clearInterval(t);
  }, [isToday]);

  // Today view: realtime INSERT subscription on operations.incidents
  // (logService pattern) — new operator marks appear without a refetch.
  useEffect(() => {
    if (!isToday) return;
    const channel = supabase
      .channel('insights-incidents-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'operations',
          table: 'incidents',
          filter: `room_id=eq.${roomId}`,
        },
        (payload) => {
          const ev = mapIncidentRow(payload.new as Record<string, any>);
          setEvents((prev) => (prev.some((e) => e.id === ev.id) ? prev : [...prev, ev]));
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [roomId, isToday]);

  // Lane list: room PCs (player order) + any PCs seen only in the data.
  const lanePcs = useMemo(() => {
    const list: { id: string; name: string }[] = pcs
      .filter((p) => !p.is_dedicated_server)
      .map((p) => ({ id: p.id, name: p.pc_name || p.id }));
    const known = new Set(list.map((l) => l.id));
    for (const s of timeline) {
      if (!known.has(s.pcInstanceId)) {
        list.push({ id: s.pcInstanceId, name: s.pcName });
        known.add(s.pcInstanceId);
      }
    }
    for (const r of runs) {
      if (r.pcInstanceId && !known.has(r.pcInstanceId)) {
        list.push({ id: r.pcInstanceId, name: r.pcName ?? 'Unknown PC' });
        known.add(r.pcInstanceId);
      }
    }
    return list;
  }, [pcs, timeline, runs]);

  const pcColor = useCallback(
    (pcId: string) => {
      const i = lanePcs.findIndex((p) => p.id === pcId);
      return PC_COLORS[(i >= 0 ? i : 0) % PC_COLORS.length];
    },
    [lanePcs]
  );

  const visibleLanes = lanePcs.filter((p) => !hiddenPcs.has(p.id));

  // Event partitions.
  const catCounts = useMemo(() => {
    const m = new Map<EventCategory, number>();
    for (const e of events) m.set(e.category, (m.get(e.category) ?? 0) + 1);
    return m;
  }, [events]);

  const activeEvents = useMemo(
    () => events.filter((e) => filters.has(e.category)),
    [events, filters]
  );
  const testWindows = useMemo(
    () => activeEvents.filter((e) => e.category === 'test_run'),
    [activeEvents]
  );
  const markerEvents = useMemo(
    () =>
      activeEvents.filter(
        (e) =>
          e.category !== 'test_run' &&
          (!e.pcInstanceId || !hiddenPcs.has(e.pcInstanceId))
      ),
    [activeEvents, hiddenPcs]
  );
  const roomEvents = useMemo(() => markerEvents.filter((e) => !e.pcInstanceId), [markerEvents]);

  // Chart panes.
  const buildPane = useCallback(
    (metric: MetricKey, overlay: 'rssi' | 'chan_util'): PaneSpec => {
      const def = METRIC_DEFS[metric];
      const cfgs: ChartSeriesConfig[] = [];
      const list: { points: (number | null)[][]; idx: number }[] = [];

      if (def.src === 'pc') {
        for (const s of timeline) {
          if (hiddenPcs.has(s.pcInstanceId)) continue;
          cfgs.push({ label: s.pcName, color: pcColor(s.pcInstanceId) });
          list.push({ points: s.points, idx: def.idx });
        }
      } else if (def.src === 'stations') {
        router.stations.forEach((s, i) => {
          cfgs.push({
            label: `${s.label ?? s.mac}${s.band ? ` ${s.band}` : ''}`,
            color: PC_COLORS[i % PC_COLORS.length],
          });
          list.push({ points: s.points, idx: def.idx });
        });
      } else {
        router.radios.forEach((r, i) => {
          cfgs.push({ label: `util ${r.band ?? '?'}`, color: PC_COLORS[i % PC_COLORS.length] });
          list.push({ points: r.points, idx: def.idx });
        });
      }

      // Router overlay on y2 when this pane shows a PC metric and data exists.
      let y2Label: string | undefined;
      if (def.src === 'pc' && overlay === 'rssi' && router.stations.length > 0) {
        router.stations.forEach((s, i) => {
          cfgs.push({
            label: `RSSI ${s.label ?? s.mac}${s.band ? ` ${s.band}` : ''}`,
            color: PC_COLORS[i % PC_COLORS.length],
            scale: 'y2',
            dash: [5, 4],
            width: 1,
          });
          list.push({ points: s.points, idx: 1 });
        });
        y2Label = 'RSSI (dBm)';
      }
      if (def.src === 'pc' && overlay === 'chan_util' && router.radios.length > 0) {
        router.radios.forEach((r, i) => {
          cfgs.push({
            label: `util ${r.band ?? '?'}`,
            color: PC_COLORS[i % PC_COLORS.length],
            scale: 'y2',
            dash: [2, 3],
            width: 1,
          });
          list.push({ points: r.points, idx: 1 });
        });
        y2Label = 'Channel util (%)';
      }

      return {
        series: cfgs,
        data: buildAligned(list),
        yLabel: `${def.label} (${def.unit})`,
        y2Label,
      };
    },
    [timeline, router, hiddenPcs, pcColor]
  );

  const paneA = useMemo(() => buildPane(metricA, 'rssi'), [buildPane, metricA]);
  const paneB = useMemo(
    () => (metricB === 'none' ? null : buildPane(metricB, 'chan_util')),
    [buildPane, metricB]
  );

  const chartMarkers: ChartMarker[] = useMemo(
    () =>
      markerEvents.map((e) => ({
        ts: e.ts,
        color: CATEGORY_STYLE[e.category].color,
        shape: CATEGORY_STYLE[e.category].shape,
      })),
    [markerEvents]
  );

  const chartBands: ChartBand[] = useMemo(
    () =>
      testWindows.map((t) => ({
        from: t.ts,
        to: t.endTs ?? Math.min(Date.now() / 1000, to),
        color: 'rgba(138,151,166,0.14)',
      })),
    [testWindows, to]
  );

  const timeRange: [number, number] = useMemo(() => [from, to], [from, to]);

  // Zoom handlers (drag-select / double-click / drawer ±5 min).
  const handleZoom = useCallback(
    (f: number | null, t: number | null) => {
      if (f == null || t == null) {
        setRange(null);
        return;
      }
      let lo = Math.max(Math.min(f, t), dayStartSec);
      let hi = Math.min(Math.max(f, t), dayEndSec);
      if (hi - lo < 30) {
        const mid = (lo + hi) / 2;
        lo = mid - 15;
        hi = mid + 15;
      }
      setRange([lo, hi]);
    },
    [dayStartSec, dayEndSec]
  );

  const shiftDate = (delta: number) => {
    const d = new Date(`${date}T12:00:00`);
    d.setDate(d.getDate() + delta);
    setDate(fmtDateLocal(d));
    setRange(null);
    setSelectedEvent(null);
  };

  const toggleFilter = (cat: EventCategory) => {
    setFilters((prev) => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      return next;
    });
  };

  const togglePc = (id: string) => {
    setHiddenPcs((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const runsByPc = useCallback(
    (pcId: string) => runs.filter((r) => r.pcInstanceId === pcId),
    [runs]
  );
  const eventsByPc = useCallback(
    (pcId: string) => markerEvents.filter((e) => e.pcInstanceId === pcId),
    [markerEvents]
  );

  const selectedPcName =
    selectedEvent?.pcInstanceId != null
      ? lanePcs.find((p) => p.id === selectedEvent.pcInstanceId)?.name ?? null
      : null;

  const hasSeries = paneA.series.length > 0 || (paneB?.series.length ?? 0) > 0;

  return (
    <>
      {/* Toolbar */}
      <div style={card}>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={labelStyle}>Date</label>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button className="refresh-btn" onClick={() => shiftDate(-1)} title="Previous day">
                ‹
              </button>
              <input
                type="date"
                value={date}
                max={todayStr}
                onChange={(e) => {
                  if (e.target.value) {
                    setDate(e.target.value);
                    setRange(null);
                    setSelectedEvent(null);
                  }
                }}
                style={{ ...selectStyle, padding: '5px 8px' }}
              />
              <button className="refresh-btn" onClick={() => shiftDate(1)} disabled={isToday} title="Next day">
                ›
              </button>
              <button
                className="refresh-btn"
                onClick={() => {
                  setDate(todayStr);
                  setRange(null);
                }}
                style={isToday ? { background: '#2B6AC0', color: '#fff' } : undefined}
              >
                Today
              </button>
            </div>
          </div>

          <div>
            <label style={labelStyle}>Metric A</label>
            <select style={selectStyle} value={metricA} onChange={(e) => setMetricA(e.target.value as MetricKey)}>
              {METRIC_ORDER.map((k) => (
                <option key={k} value={k}>
                  {METRIC_DEFS[k].label} ({METRIC_DEFS[k].unit})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={labelStyle}>Metric B</label>
            <select
              style={selectStyle}
              value={metricB}
              onChange={(e) => setMetricB(e.target.value as MetricKey | 'none')}
            >
              <option value="none">— none —</option>
              {METRIC_ORDER.map((k) => (
                <option key={k} value={k}>
                  {METRIC_DEFS[k].label} ({METRIC_DEFS[k].unit})
                </option>
              ))}
            </select>
          </div>

          <div style={{ flex: 1 }} />

          <div style={{ textAlign: 'right' }}>
            <label style={labelStyle}>Window</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: '#54626f', fontVariant: 'tabular-nums' }}>
                {fmtHM(from)}–{fmtHM(to)} · {bucket}s buckets
              </span>
              <button className="refresh-btn" onClick={() => setRange(null)} disabled={!range}>
                Reset zoom
              </button>
            </div>
          </div>
        </div>

        {/* PC toggles */}
        {lanePcs.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 14 }}>
            {lanePcs.map((p) => {
              const visible = !hiddenPcs.has(p.id);
              return (
                <button
                  key={p.id}
                  className="refresh-btn"
                  onClick={() => togglePc(p.id)}
                  style={{
                    fontSize: 12,
                    padding: '4px 10px',
                    opacity: visible ? 1 : 0.45,
                    borderLeft: `4px solid ${pcColor(p.id)}`,
                  }}
                  title={visible ? 'Click to hide' : 'Click to show'}
                >
                  {p.name}
                </button>
              );
            })}
          </div>
        )}

        {/* Category filter chips with counts */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
          {CHIP_ORDER.map((cat) => {
            const st = CATEGORY_STYLE[cat];
            const on = filters.has(cat);
            const count = catCounts.get(cat) ?? 0;
            const wifiDisabled = cat === 'wifi' && durS >= 7200;
            return (
              <button
                key={cat}
                className="refresh-btn"
                onClick={() => toggleFilter(cat)}
                disabled={wifiDisabled}
                title={
                  wifiDisabled
                    ? 'Zoom in under 2 h to load raw WiFi events'
                    : `${st.label}: ${count}`
                }
                style={{
                  fontSize: 12,
                  padding: '4px 10px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  opacity: wifiDisabled ? 0.4 : on ? 1 : 0.45,
                  background: on ? `${st.color}14` : undefined,
                  border: `1px solid ${on ? st.color : '#d5dce5'}`,
                  borderRadius: 100,
                  color: '#16202e',
                }}
              >
                <span style={{ color: st.color }}>{st.glyph}</span>
                {st.label}
                <span style={{ color: '#8a97a6', fontVariant: 'tabular-nums' }}>{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Run/scene strips */}
      <div style={card}>
        <label style={labelStyle}>Runs & events per PC</label>
        {loading && runs.length === 0 && events.length === 0 ? (
          <>
            <Skeleton height={26} />
            <Skeleton height={26} />
            <Skeleton height={26} />
          </>
        ) : (
          <div style={{ display: 'grid', gap: 4 }}>
            {visibleLanes.map((p) => (
              <RunSceneStrip
                key={p.id}
                label={p.name}
                runs={runsByPc(p.id)}
                testWindows={testWindows}
                events={eventsByPc(p.id)}
                range={timeRange}
                onSelectEvent={setSelectedEvent}
                selectedEventId={selectedEvent?.id ?? null}
              />
            ))}
            {roomEvents.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: '96px 1fr', gap: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#8a97a6', lineHeight: '18px' }}>
                  Room
                </div>
                <EventMarkerRow
                  events={roomEvents}
                  range={timeRange}
                  onSelect={setSelectedEvent}
                  selectedEventId={selectedEvent?.id ?? null}
                />
              </div>
            )}
            {visibleLanes.length === 0 && (
              <span style={{ color: '#8a97a6', fontSize: 13 }}>No PCs for this room.</span>
            )}
          </div>
        )}
      </div>

      {/* Chart panes */}
      <div style={card}>
        {loading && timeline.length === 0 ? (
          <>
            <Skeleton height={200} />
            <Skeleton height={12} />
          </>
        ) : !hasSeries ? (
          <div style={{ padding: '18px 0', color: '#8a97a6' }}>
            No telemetry in this window
            {METRIC_DEFS[metricA].src !== 'pc' ? ' (router data appears once the room poller is live)' : ''}.
            Drag on a chart to zoom · double-click to reset.
          </div>
        ) : (
          <>
            {paneA.series.length > 0 && (
              <UPlotChart
                series={paneA.series}
                data={paneA.data}
                timeRange={timeRange}
                yLabel={paneA.yLabel}
                y2Label={paneA.y2Label}
                markers={chartMarkers}
                bands={chartBands}
                syncKey={`insights-${roomId}`}
                onZoom={handleZoom}
                height={230}
              />
            )}
            {paneB && paneB.series.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <UPlotChart
                  series={paneB.series}
                  data={paneB.data}
                  timeRange={timeRange}
                  yLabel={paneB.yLabel}
                  y2Label={paneB.y2Label}
                  markers={chartMarkers}
                  bands={chartBands}
                  syncKey={`insights-${roomId}`}
                  onZoom={handleZoom}
                  height={200}
                />
              </div>
            )}
            <div style={{ fontSize: 11, color: '#8a97a6', marginTop: 8 }}>
              Drag to zoom · double-click to reset · vertical lines are events (colors match the chips)
              {isToday ? ' · live: refreshes every 60 s' : ''}
            </div>
          </>
        )}
      </div>

      {selectedEvent && (
        <EventDetailDrawer
          event={selectedEvent}
          pcName={selectedPcName}
          onZoom={(f, t) => handleZoom(f, t)}
          onClose={() => setSelectedEvent(null)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Page shell — tabs
// ---------------------------------------------------------------------------

export default function InsightsView({ roomId, operator }: InsightsViewProps) {
  const [tab, setTab] = useState<'timeline' | 'scenes' | 'trends'>('timeline');
  void operator; // reserved (page is already admin-gated in App.tsx)

  if (!roomId) {
    return <div style={{ padding: 24 }}>Select a room to view insights.</div>;
  }

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
      <div style={{ maxWidth: 1180, margin: '0 auto', padding: '20px 16px 60px' }}>
        <style>{'@keyframes insightsPulse { 0%,100% { opacity: .4 } 50% { opacity: .9 } }'}</style>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <h2 style={{ margin: 0 }}>Insights</h2>
          <span
            style={{
              background: '#FBEEDA',
              color: '#7a4310',
              fontSize: 11,
              fontWeight: 700,
              padding: '3px 9px',
              borderRadius: 100,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            Admin
          </span>
        </div>
        <p style={{ color: '#54626f', marginTop: 0, fontSize: 14 }}>
          Room-day timeline of telemetry, runs and every event marker (auto-detected disconnects, operator
          marks, router events) on one axis — plus per-scene and 30-day trend reports.
        </p>

        <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
          {(
            [
              ['timeline', 'Timeline'],
              ['scenes', 'Scenes'],
              ['trends', 'Trends'],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              className="refresh-btn"
              onClick={() => setTab(key)}
              style={tab === key ? { background: '#2B6AC0', color: '#fff', fontWeight: 600 } : undefined}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === 'timeline' && <TimelineTab roomId={roomId} />}
        {tab === 'scenes' && <ScenesTab roomId={roomId} />}
        {tab === 'trends' && <TrendsTab roomId={roomId} />}
      </div>
    </div>
  );
}
